import { Slot } from '@radix-ui/react-slot';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// Disabled buttons must remain legible. Each visual family owns an opaque
// disabled treatment rather than relying on an inherited opacity reduction.
const disabledFilledButton =
  'disabled:opacity-100 disabled:border-transparent disabled:bg-muted disabled:text-foreground/65 aria-disabled:opacity-100 aria-disabled:border-transparent aria-disabled:bg-muted aria-disabled:text-foreground/65';
const disabledOutlineButton =
  'disabled:opacity-100 disabled:border-border/60 disabled:bg-muted/50 disabled:text-foreground/65 aria-disabled:opacity-100 aria-disabled:border-border/60 aria-disabled:bg-muted/50 aria-disabled:text-foreground/65';
const disabledTextButton =
  'disabled:opacity-100 disabled:bg-transparent disabled:text-muted-foreground aria-disabled:opacity-100 aria-disabled:bg-transparent aria-disabled:text-muted-foreground';
const disabledLinkButton =
  'disabled:opacity-100 disabled:text-muted-foreground disabled:no-underline aria-disabled:opacity-100 aria-disabled:text-muted-foreground aria-disabled:no-underline';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:cursor-not-allowed aria-disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        // The app's primary CTA is the dark neutral (Upload, Connect, Save…) — NOT
        // the blue --primary (that hue is licensed only for the ModePill active
        // state). default === neutral so a bare <Button> is always on-brand.
        default: `bg-neutral text-neutral-foreground border border-neutral-border hover:bg-neutral-hover ${disabledFilledButton}`,
        destructive: `bg-destructive text-destructive-foreground hover:bg-destructive/90 ${disabledFilledButton}`,
        outline: `border border-input bg-background hover:bg-accent hover:text-accent-foreground ${disabledOutlineButton}`,
        secondary: `bg-secondary text-secondary-foreground hover:bg-secondary/80 ${disabledFilledButton}`,
        neutral: `bg-neutral text-neutral-foreground border border-neutral-border hover:bg-neutral-hover ${disabledFilledButton}`,
        ghost: `hover:bg-accent hover:text-accent-foreground ${disabledTextButton}`,
        flat: `text-muted-foreground hover:bg-accent hover:text-accent-foreground ${disabledTextButton}`,
        link: `text-primary underline-offset-4 hover:underline ${disabledLinkButton}`,
      },
      size: {
        // Compact pill. Desktop band is 32px default / 28px sm, measured against
        // marketer.com (28-32px) and the repo's own call sites, 131 of which
        // already pushed this variant DOWN to h-8/h-7 by hand.
        // Use size="classic" (below) if you genuinely need the old h-11 rectangle.
        default: 'h-11 md:h-8 rounded-full px-3.5 text-sm gap-2',
        sm: 'h-11 md:h-7 rounded-full px-3 text-caption gap-1.5',
        lg: 'h-12 md:h-11 rounded-full px-5 text-sm gap-2',
        icon: 'h-11 w-11 md:h-8 md:w-8 rounded-full',
        'icon-sm': 'h-11 w-11 md:h-7 md:w-7 rounded-full',
        chip: 'h-7 px-2.5 py-0 text-caption gap-1 rounded-full',
        // Overriding to a smaller desktop size? Repeat the `md:` prefix too
        // (e.g. `size-6 md:size-6`) — a bare `size-6` override never beats
        // this variant's own `md:size-7` at desktop widths (tailwind-merge
        // only dedupes within matching modifier groups).
        'toolbar-icon': 'size-11 md:size-7 p-0 rounded-full [&_svg]:size-5 md:[&_svg]:size-4',
        // Mobile footprint matches nav-icon-mobile (40px) — the same bump the
        // sidebar/header icons got; composer-icon-mobile (36px) is kept only
        // as the CSS var other call sites may still reference. Desktop is
        // unchanged (composer-icon-desktop === nav-icon-desktop, both 28px).
        'composer-icon':
          'size-nav-icon-mobile md:size-composer-icon-desktop p-0 rounded-full [&_svg]:size-5 md:[&_svg]:size-4',
        'nav-icon':
          'size-nav-icon-mobile md:size-nav-icon-desktop p-0 rounded-full [&_svg]:size-5 md:[&_svg]:size-4',
        // `h-11 md:h-8`, not a bare `h-8`: this variant had no responsive half at
        // all, so every compact button was a 32px tap target on a phone — under
        // the 44px floor — and 16 call sites were hand-patching it with
        // `min-h-11`.
        compact: 'h-11 md:h-8 rounded-full gap-1.5 px-3.5 text-caption',
        'icon-compact': 'size-11 md:size-8 rounded-full p-0',
        // Escape hatch for the legacy h-11 rounded-md rectangle.
        // Do NOT use for new code — add it per-call as `size="classic"`.
        classic: 'h-11 md:h-10 px-4 py-2 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Lets a focusable aria-disabled control explain its unmet requirements on
   * activation. Use only when the click is a validation affordance; it must
   * never perform the protected action.
   */
  allowAriaDisabledActivation?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      allowAriaDisabledActivation = false,
      onClick,
      onClickCapture,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isAriaDisabled = props['aria-disabled'] === true || props['aria-disabled'] === 'true';

    if (asChild && isAriaDisabled) {
      throw new Error(
        'Button with aria-disabled cannot use asChild. Use a native Button so the shared primitive can prevent activation.',
      );
    }

    const preventAriaDisabledActivation: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        onClick={
          isAriaDisabled && !allowAriaDisabledActivation ? preventAriaDisabledActivation : onClick
        }
        onClickCapture={
          isAriaDisabled && !allowAriaDisabledActivation
            ? preventAriaDisabledActivation
            : onClickCapture
        }
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
